import { Box, Flex, Text } from '@chakra-ui/react';

import {
  DIVERGING_BAR_HEADER_LABEL_HEIGHT,
  DivergingBarChart,
  type DivergingBarData,
  type DivergingBarLayout,
  FormatType,
  useChartTheme,
  withExplicitSign,
} from '@revelio/replots';

import { usePlotMetric } from '../../hooks/usePlotMetric';
import type { BreakdownSingleDataSet } from '../DataProvider/types';
import { isSingleDataSet } from '../DataProvider/types';
import type { DivergingBarComboSortKey } from '../PageConfigProvider/types/item';
import { isTransitionMetric } from '../PageConfigProvider/types/metric';
import { useChart } from './ChartProvider';
import styles from './DivergingBarCombo.module.css';
import { getPrettyFormatValue } from './utils/format';
import { getFormat } from './utils/getFormat';
import { getDivergingBarConfig } from './utils/map-to-diverging-data';
import { transformComparisonData } from './utils/transform-comparison-data';
import { useActiveColors } from './utils/useActiveColors';

const FOOTER_HEIGHT = 25;

const TORNADO_FLEX = 1.5;
const DIFFERENCE_FLEX = 0.5;

function sortByLabelOrder<T extends { label: string }>(
  data: T[],
  labelOrder: string[]
): T[] {
  const orderMap = new Map(labelOrder.map((l, i) => [l, i]));
  return [...data].sort(
    (a, b) =>
      (orderMap.get(a.label) ?? Infinity) - (orderMap.get(b.label) ?? Infinity)
  );
}

function getSortValue(
  data: DivergingBarData,
  by: DivergingBarComboSortKey
): number {
  const left = data.leftValue ?? 0;
  const right = data.rightValue ?? 0;
  switch (by) {
    case 'left':
      return left;
    case 'right':
      return right;
    case 'diff':
      return left + right;
    case 'sum':
      return Math.abs(left) + Math.abs(right);
  }
}

export const DivergingBarCombo = ({
  dataSet,
}: {
  dataSet: BreakdownSingleDataSet;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { colorMap } = useActiveColors();
  const { isNewTheme } = useChartTheme();
  const format = (() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    return getFormat({ metric });
  })();

  const differenceFormat = (() => {
    if (format === FormatType.PERCENTAGE) {
      return FormatType.PERCENTAGE_POINT;
    }
    if (metric === 'transitions_inflows_and_outflows') {
      return FormatType.SI;
    }
    return FormatType.DECIMAL;
  })();

  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const baseline = item?.plot?.comparisonTransformation?.baseline ?? 'second';

  const tornadoConfig = getDivergingBarConfig(
    dataSet,
    item,
    colorMap,
    getPrettyFormatValue(format),
    { baseline, dualViewColors: true }
  );

  const transformedDifference = transformComparisonData(dataSet, {
    baseline,
  });
  if (!isSingleDataSet(transformedDifference)) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Text fontSize="sm" color="gray.600">
          Incompatible data for comparison
        </Text>
      </Box>
    );
  }

  const differenceDataSet = transformedDifference;
  const differenceFormatValue = (() => {
    const base = getPrettyFormatValue(differenceFormat);
    if (metric === 'transitions_inflows_and_outflows') {
      return withExplicitSign(base);
    }
    return base;
  })();
  const differenceConfig = getDivergingBarConfig(
    differenceDataSet,
    item,
    colorMap,
    differenceFormatValue
  );

  const comboSort = item?.plot?.divergingBarCombo?.sort;
  const sortedLabels = (() => {
    if (!comboSort) {
      // Sort descending by absolute baseline share so the highest-share row appears first.
      // tornadoConfig stores leftValue as the negated baseline share,
      // so Math.abs recovers the original share magnitude.
      return [...tornadoConfig.divergingBarData]
        .sort((a, b) => Math.abs(b.leftValue ?? 0) - Math.abs(a.leftValue ?? 0))
        .map((d) => d.label);
    }
    const source =
      comboSort.by === 'diff'
        ? differenceConfig.divergingBarData
        : tornadoConfig.divergingBarData;
    const directionFactor = comboSort.direction === 'asc' ? 1 : -1;
    return [...source]
      .sort(
        (a, b) =>
          directionFactor *
          (getSortValue(a, comboSort.by) - getSortValue(b, comboSort.by))
      )
      .map((d) => d.label);
  })();

  const tornadoDataSorted = sortByLabelOrder(
    tornadoConfig.divergingBarData,
    sortedLabels
  );
  const differenceDataSorted = sortByLabelOrder(
    differenceConfig.divergingBarData,
    sortedLabels
  );

  const combo = item?.plot?.divergingBarCombo;
  const comparisonLabel = combo?.comparisonLabel?.trim() ?? '';
  const differenceLabel = combo?.differenceLabel?.trim() ?? '';

  const renderCenteredFooter =
    (label: string) => (_layout: DivergingBarLayout) => (
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" fontWeight={600}>
          {label}
        </Text>
      </Box>
    );

  const showEitherFooter = comparisonLabel !== '' || differenceLabel !== '';
  const showLeftFooter = comparisonLabel !== '';
  const showRightFooter = differenceLabel !== '';

  const isTransition = isTransitionMetric(metric);

  const tornadoByLabel = (() => {
    const map = new Map<string, DivergingBarData>();
    for (const row of tornadoDataSorted) {
      map.set(row.label, row);
    }
    return map;
  })();
  const differenceByLabel = (() => {
    const map = new Map<string, DivergingBarData>();
    for (const row of differenceDataSorted) {
      map.set(row.label, row);
    }
    return map;
  })();

  const tooltipBaseFormatType =
    format === FormatType.SI ? FormatType.INTEGER : format;
  const formatTooltipBase = getPrettyFormatValue(tooltipBaseFormatType);

  const tooltipDifferenceFormatType =
    differenceFormat === FormatType.SI ? FormatType.INTEGER : differenceFormat;
  const formatTooltipDifference = (() => {
    const base = getPrettyFormatValue(tooltipDifferenceFormatType);
    if (isTransition) {
      return withExplicitSign(base);
    }
    return base;
  })();

  const differenceLineLabel = isTransition ? 'Change' : 'Difference';

  const renderRowTooltip = (data: DivergingBarData) => {
    const tornadoRow = tornadoByLabel.get(data.label);
    const diffRow = differenceByLabel.get(data.label);

    const hasDiff = diffRow !== undefined;
    const diffValue = diffRow
      ? (diffRow.leftValue ?? 0) + (diffRow.rightValue ?? 0)
      : 0;

    const leftRaw = tornadoRow?.leftValue;
    const rightRaw = tornadoRow?.rightValue;
    const hasLeft = typeof leftRaw === 'number';
    const hasRight = typeof rightRaw === 'number';

    return (
      <div className={styles.tooltipBlock}>
        <div className={styles.tooltipTitle}>{data.label}</div>
        <dl className={styles.tooltipMetrics}>
          {hasDiff && (
            <>
              <dt>{differenceLineLabel}</dt>
              <dd>{formatTooltipDifference(diffValue)}</dd>
            </>
          )}
          {hasRight && tornadoRow && (
            <>
              <dt>{tornadoRow.rightLabel}</dt>
              <dd>{formatTooltipBase(Math.abs(rightRaw))}</dd>
            </>
          )}
          {hasLeft && tornadoRow && (
            <>
              <dt>{tornadoRow.leftLabel}</dt>
              <dd>{formatTooltipBase(Math.abs(leftRaw))}</dd>
            </>
          )}
        </dl>
      </div>
    );
  };

  const tornadoLeftHeaderLabel = (() => {
    const configured = combo?.leftHeaderLabel?.trim();
    if (configured) return configured;
    return tornadoConfig.leftFooterLabel;
  })();
  const tornadoRightHeaderLabel = (() => {
    const configured = combo?.rightHeaderLabel?.trim();
    if (configured) return configured;
    return tornadoConfig.rightFooterLabel;
  })();

  const tornadoHasHeaderLabels =
    (tornadoLeftHeaderLabel !== undefined && tornadoLeftHeaderLabel !== '') ||
    (tornadoRightHeaderLabel !== undefined && tornadoRightHeaderLabel !== '');
  const differenceTopOffset = tornadoHasHeaderLabels
    ? DIVERGING_BAR_HEADER_LABEL_HEIGHT
    : 0;

  return (
    <Flex
      width="100%"
      height="100%"
      flexDirection="row"
      gap={4}
      position="relative"
      overflow="hidden"
    >
      <Flex
        flex={TORNADO_FLEX}
        minWidth={0}
        flexDirection="column"
        position="relative"
      >
        <Box flex="1 1 auto" minHeight={0}>
          <DivergingBarChart
            data={tornadoDataSorted}
            format={format}
            leftColor={tornadoConfig.leftColor}
            rightColor={tornadoConfig.rightColor}
            tooltipContent={renderRowTooltip}
            showBarLabels={showBarLabels}
            valueLabel={tornadoConfig.valueLabel}
            sortData={false}
            leftHeaderLabel={tornadoLeftHeaderLabel}
            rightHeaderLabel={tornadoRightHeaderLabel}
            maxLabelWidthRatio={0.45}
            barCornerRadius={isNewTheme ? 0 : undefined}
            centerGap={isNewTheme ? 3 : undefined}
            barHeightRatio={isNewTheme ? 0.65 : undefined}
            barLabelPosition={isNewTheme ? 'outside' : undefined}
            barLabelColor={isNewTheme ? 'var(--ui-text-normal)' : undefined}
            footer={
              showLeftFooter ? renderCenteredFooter(comparisonLabel) : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </Box>
      </Flex>
      <Flex
        flex={DIFFERENCE_FLEX}
        minWidth={0}
        flexDirection="column"
        position="relative"
      >
        <Box flex="1 1 auto" minHeight={0}>
          <DivergingBarChart
            data={differenceDataSorted}
            format={differenceFormat}
            leftColor={isNewTheme ? undefined : differenceConfig.leftColor}
            rightColor={isNewTheme ? undefined : differenceConfig.rightColor}
            tooltipContent={renderRowTooltip}
            showBarLabels={showBarLabels}
            valueLabel={differenceConfig.valueLabel}
            sortData={false}
            topOffset={differenceTopOffset}
            showYAxisLabels={false}
            barOpacity={0.9}
            barCornerRadius={isNewTheme ? 0 : undefined}
            barHeightRatio={isNewTheme ? 0.65 : undefined}
            barLabelPosition={isNewTheme ? 'outside' : undefined}
            labelReserveRatio={isNewTheme ? 0.6 : undefined}
            footer={
              showRightFooter
                ? renderCenteredFooter(differenceLabel)
                : undefined
            }
            footerHeight={showEitherFooter ? FOOTER_HEIGHT : undefined}
          />
        </Box>
      </Flex>
    </Flex>
  );
};
